import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatBytes, type UploadedAttachment } from '../lib/attachments'
import { AttachmentModal } from './AttachmentModal'
import { useGallery } from './gallery'
import { fullPreviewKindOf } from './previewKind'
import type { PreviewItem } from './types'

/**
 * Gonderilmis bir mesajin ekleri.
 *
 * ## `<img src={url}>` - base64 DEGIL
 *
 * Onceki halde resimler mesaj icinde base64 data-URL olarak tasiniyordu ve
 * 3 resimli bir konusma HER TURDA ~4 MB yeniden yukleniyordu. Artik URL
 * uzerinden geliyorlar, tarayici onbellekliyor (`immutable`, icerik adresli)
 * ve prompt kurarken sunucu kendisi data-URL'e ceviriyor - frontend bir daha
 * asla base64 uretmiyor veya gormuyor.
 *
 * ## `width`/`height` ZORUNLU
 *
 * Sunucudan gelen gercek boyutlar layout shift'i onluyor. Inline-base64
 * surumunde bu bilgi YOKTU, yani bu naif bir port'un EKLEYECEGI bir problem:
 * resimler yuklenince mesaj listesi ziplardi.
 *
 * ## `foreign`
 *
 * Konusma baska bir kullanicidan kopyalandiysa/catallandiysa ekler de oradan
 * geliyor: HTML onizlemesi once uyari gosteriyor (bkz. `DocumentView`).
 */
export function MessageAttachments({
  items,
  foreign = false,
}: {
  items: UploadedAttachment[]
  foreign?: boolean
}) {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  // Konusma genelindeki galeri: modal TUM eklerle aciliyor, sag-sol gezinme
  // calisiyor. Galeri yoksa (paylasim ekrani) asagidaki yerel modal devrede.
  const gallery = useGallery()

  const previews = useMemo<PreviewItem[]>(
    () =>
      items.map((item) => {
        const fullPreviewKind = fullPreviewKindOf(item.filename, item.mime)
        return {
          kind: item.kind,
          filename: item.filename,
          mime: item.mime,
          byteSize: item.byte_size,
          previewUrl: item.url,
          downloadUrl: item.url,
          width: item.width,
          height: item.height,
          detailUrl: `/api/attachments/${item.id}/detail`,
          fullPreviewKind: fullPreviewKind ?? undefined,
          fullPreviewUrl: fullPreviewKind ? `/api/attachments/${item.id}/preview` : undefined,
          warnHtml: foreign,
        }
      }),
    [items, foreign],
  )

  if (items.length === 0) return null

  return (
    <>
      <ul className="msg-attachments">
        {items.map((item, index) => (
          <li key={item.id}>
            <button
              type="button"
              className={
                item.kind === 'image'
                  ? 'att att-image'
                  : item.kind === 'video' && item.thumb_url
                    ? 'att att-image att-video'
                    : 'att att-doc'
              }
              onClick={() => {
                if (!gallery?.openById(item.id)) setOpenIndex(index)
              }}
              aria-label={t('attach.preview.label', { name: item.filename })}
            >
              {item.kind === 'video' && item.thumb_url ? (
                // Kucuk resim = videonun ILK KARESI (cozumlemede zaten
                // uretildi). Ustundeki ucgen bunun bir video oldugunu
                // soyluyor; tiklayinca modalde oynatiliyor.
                <>
                  <img src={item.thumb_url} alt={item.filename} loading="lazy" />
                  <span className="att-play" aria-hidden="true">
                    <PlayIcon />
                  </span>
                </>
              ) : item.kind === 'image' ? (
                <img
                  src={item.url}
                  alt={item.filename}
                  // Gercek boyutlar: layout shift'i onluyor.
                  width={item.width ?? undefined}
                  height={item.height ?? undefined}
                  // Gecmiste yukari kaydirilmis resimler bosa cekilmesin.
                  loading="lazy"
                />
              ) : (
                <>
                  <span className="att-icon" aria-hidden="true">
                    {item.kind === 'video' ? <PlayIcon /> : <DocIcon />}
                  </span>
                  <span className="att-info">
                    <span className="att-name">{item.filename}</span>
                    <span className="att-size">
                      {formatBytes(item.byte_size)}
                      {item.kind === 'video'
                        ? item.extract_status === 'pending'
                          ? ` · ${t('attach.video.analyzing')}`
                          : item.page_image_count > 0
                            ? ` · ${t('attach.video.frames', { count: item.page_image_count })}`
                            : ''
                        : item.page_image_count > 0 && ` · ${item.page_image_count} sayfa`}
                      {item.extract_status === 'failed' && t('attach.preview.extractFailed')}
                    </span>
                  </span>
                </>
              )}
            </button>
          </li>
        ))}
      </ul>

      {openIndex !== null && (
        <AttachmentModal
          items={previews}
          startIndex={openIndex}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </>
  )
}

/** Video chip'i: belge ikonu yerine oynatma ucgeni - tur bir bakista belli olsun. */
function PlayIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 9.5v5l4-2.5-4-2.5Z" fill="currentColor" />
    </svg>
  )
}

function DocIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  )
}
